import React, { useState } from 'react';
import { ClockIcon, MapPinIcon, MagnifyingGlassIcon, DocumentTextIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '@/ui/components/atoms/Button';
import { PlanningExpertise } from '../../types/planning.types';
import './ExpertiseCalendarCard.styles.css';

interface ExpertiseCalendarCardProps {
  expertise: PlanningExpertise;
  onDetailClick?: () => void;
  onFMClick?: () => void;
}

export const ExpertiseCalendarCard: React.FC<ExpertiseCalendarCardProps> = ({
  expertise,
  onDetailClick,
  onFMClick
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top: rect.bottom + 5,
      left: Math.min(rect.right - 200, window.innerWidth - 210) // Ensure tooltip stays within viewport
    });
    setShowTooltip(true);
  };

  const getCardClassName = () => {
    const classes = ['expert-planning-card'];
    
    if (expertise.status === 'completed') {
      classes.push('expert-planning-card--completed');
    }
    
    return classes.join(' ');
  };

  const getTypeLabel = () => {
    const typeMap: Record<string, string> = {
      'EX': 'Expertise',
      'CT': 'Contre-visite',
      'ES': 'Estimation',
      'default': expertise.type
    };
    return typeMap[expertise.type] || typeMap.default;
  };

  return (
    <div className={getCardClassName()}>
      <div className="expert-planning-card__content">
        {/* Time */}
        {expertise.heure_rv && (
          <div className="expert-planning-card__time">
            <ClockIcon className="expert-planning-card__time-icon" />
            <span>{expertise.heure_rv}</span>
          </div>
        )}

        {/* Type badge */}
        <div className="expert-planning-card__type">
          {getTypeLabel()}
        </div>

        {/* Dossier ID */}
        {expertise.k_id_dossier && (
          <div className="expert-planning-card__id">
            #{expertise.k_id_dossier}
          </div>
        )}

        {/* Remarks icon with tooltip */}
        {expertise.remarques_rv && (
          <div className="expert-planning-card__remarks">
            <div 
              className="expert-planning-card__remarks-icon-wrapper"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <InformationCircleIcon className="expert-planning-card__remarks-icon" />
              {showTooltip && (
                <div 
                  className="expert-planning-card__remarks-tooltip"
                  style={{ 
                    top: `${tooltipPosition.top}px`, 
                    left: `${tooltipPosition.left}px` 
                  }}
                >
                  {expertise.remarques_rv}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Address */}
        {expertise.address && expertise.address.length > 0 && (
          <div className="expert-planning-card__address">
            <MapPinIcon className="expert-planning-card__address-icon" />
            <span className="expert-planning-card__address-text">
              {expertise.address[0]}
            </span>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="expert-planning-card__actions">
        <Button
          variant="secondary"
          size="small"
          onClick={onDetailClick}
          className="expert-planning-card__action"
        >
          <MagnifyingGlassIcon className="w-3 h-3" />
          Détail
        </Button>
        <Button
          variant="secondary"
          size="small"
          onClick={onFMClick}
          className="expert-planning-card__action expert-planning-card__action--fm"
        >
          <DocumentTextIcon className="w-3 h-3" />
          FM
        </Button>
      </div>
    </div>
  );
};